import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectExtended, ProjectsExtendedResponse } from '../../projects/types';
import { adminProjectsApi } from '../../projects/admin-api';
import { syntaxApi } from '../../spatial/services/syntax-api.service';
import { ProjectExpandableRow } from './ProjectExpandableRow';
import './project-expandable.css';

const AUTO_REFRESH_INTERVAL = 5000;

export interface ProjectsTableHandle {
  reload: () => Promise<void>;
}

interface ProjectsTableExtendedProps {
  deletingProjectId?: string | null;
  onEdit: (projectId: string) => void;
  onDelete: (projectId: string) => void;
  onCreate: () => void;
  onReconcileStuck?: () => void;
  reconcilingStuck?: boolean;
}

export const ProjectsTableExtended = forwardRef<ProjectsTableHandle, ProjectsTableExtendedProps>(
  ({ deletingProjectId, onEdit, onDelete, onCreate, onReconcileStuck, reconcilingStuck = false }, ref) => {
    const { t } = useTranslation('admin');
    const [data, setData] = useState<ProjectsExtendedResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [retryingId, setRetryingId] = useState<string | null>(null);
    const [retrySyntaxId, setRetrySyntaxId] = useState<string | null>(null);
    const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
    const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadData = useCallback(async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        }
        setError(null);
        const response = await adminProjectsApi.listExtended();
        setData(response);
      } catch (err) {
        if (!silent) {
          setError(err instanceof Error ? err.message : 'Error al cargar proyectos');
        }
        console.error('Error loading extended projects:', err);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    }, []);

    useImperativeHandle(ref, () => ({
      reload: async () => {
        await loadData(false);
      }
    }), [loadData]);

    useEffect(() => {
      void loadData();
    }, [loadData]);

    useEffect(() => {
      const hasBuildingProjects = data?.projects.some(
        (p) =>
          p.pipeline.overallStatus === 'building' ||
          p.pipeline.overallStatus === 'pending'
      );

      if (hasBuildingProjects && !autoRefreshRef.current) {
        setIsAutoRefreshing(true);
        autoRefreshRef.current = setInterval(() => {
          void loadData(true);
        }, AUTO_REFRESH_INTERVAL);
      } else if (!hasBuildingProjects && autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
        setIsAutoRefreshing(false);
      }

      return () => {
        if (autoRefreshRef.current) {
          clearInterval(autoRefreshRef.current);
          autoRefreshRef.current = null;
        }
      };
    }, [data, loadData]);

    const handleDelete = (projectId: string) => {
      if (deletingId === projectId) {
        // Second click: execute deletion, reset confirmation state
        setDeletingId(null);
        onDelete(projectId);
      } else {
        // First click: enter confirmation state
        setDeletingId(projectId);
        // Auto-reset after 3s if user doesn't confirm
        setTimeout(() => {
          setDeletingId((current) => current === projectId ? null : current);
        }, 3000);
      }
    };

    const handleRetrySyntax = async (projectId: string) => {
      setRetrySyntaxId(projectId);
      try {
        await syntaxApi.recalculate(projectId);
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadData();
      } catch (err) {
        console.error('Error retrying syntax:', err);
        alert('Error al reintentar el cálculo de sintaxis. Por favor, intenta nuevamente.');
      } finally {
        setRetrySyntaxId(null);
      }
    };

    const handleRetryPipeline = async (projectId: string) => {
      setRetryingId(projectId);
      try {
        console.log('Reintentando pipeline para proyecto:', projectId);
        const result = await adminProjectsApi.retryPipeline(projectId);
        console.log('Pipeline encolado con job ID:', result.jobId);
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadData();
      } catch (err) {
        console.error('Error retrying pipeline:', err);
        alert('Error al reintentar el pipeline. Por favor, intenta nuevamente.');
      } finally {
        setRetryingId(null);
      }
    };

    if (loading) {
      return (
        <div className="dashboard-table-container">
          <div className="dashboard-table-header">
            <h2 className="table-title">{t('projects.title')}</h2>
          </div>
          <div className="table-loading">
            <div className="loading-skeleton" />
            <div className="loading-skeleton" />
            <div className="loading-skeleton" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="dashboard-table-container">
          <div className="dashboard-table-header">
            <h2 className="table-title">{t('projects.title')}</h2>
          </div>
          <div className="dashboard-error">
            <p>{error}</p>
            <button type="button" className="btn-secondary" onClick={() => void loadData()}>
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    const projects = data?.projects || [];
    const summary = data?.summary;

    // Ocultar inmediatamente la fila del proyecto en eliminación
    // Esto evita deformación visual de la tabla durante el proceso
    // Si la API falla, deletingProjectId se resetea a null y la fila reaparece
    const visibleProjects = projects.filter(p => p.id !== deletingProjectId);

    return (
      <div className="dashboard-table-container">
        <div className="dashboard-table-header">
          <div className="table-header-left">
            <h2 className="table-title">{t('projects.title')}</h2>
            {summary && (
              <div className="table-summary">
                <SummaryBadge label="Total" value={summary.total} />
                <SummaryBadge label="Listos" value={summary.byPipelineStatus.ready} color="success" />
                <SummaryBadge label="En proceso" value={summary.byPipelineStatus.building} color="accent" />
                <SummaryBadge label="Error" value={summary.byPipelineStatus.error} color="error" />
              </div>
            )}
            {isAutoRefreshing && (
              <span className="auto-refresh-indicator" title="Actualizando automáticamente cada 5s">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="spin"
                >
                  <path
                    d="M8 3V1M8 15V13M3 8H1M15 8H13M4.93 4.93L3.51 3.51M12.49 12.49L11.07 11.07M4.93 11.07L3.51 12.49M12.49 3.51L11.07 4.93"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Actualizando...
              </span>
            )}
          </div>
          <div className="table-header-actions">
            {onReconcileStuck && (
              <button
                type="button"
                className="btn-secondary btn-reconcile"
                onClick={onReconcileStuck}
                disabled={reconcilingStuck}
                title="Detecta y corrige proyectos atascados en 'building' sin job activo (útil tras reinicio del backend)"
              >
                {reconcilingStuck ? 'Reconciliando...' : 'Reconciliar Stuck'}
              </button>
            )}
            <button type="button" className="btn-create" onClick={onCreate}>
              {t('projects.create')}
            </button>
          </div>
        </div>

        {visibleProjects.length === 0 ? (
          <div className="table-empty">
            <p>{t('projects.empty')}</p>
            <button type="button" className="btn-secondary" onClick={onCreate}>
              {t('projects.createFirst')}
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="dashboard-table">
              <thead className="table-head">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>N° OT</th>
                  <th>{t('projects.table.name')}</th>
                  <th>{t('projects.table.client')}</th>
                  <th style={{ textAlign: 'center' }}>Pipeline</th>
                  <th style={{ textAlign: 'right' }}>Edges</th>
                  <th style={{ textAlign: 'center' }}>Módulos</th>
                  <th style={{ textAlign: 'center' }}>Uso</th>
                  <th className="table-actions-header">{t('projects.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {visibleProjects.map((project: ProjectExtended) => (
                  <ProjectExpandableRow
                    key={project.id}
                    project={project}
                    onEdit={() => onEdit(project.id)}
                    onDelete={() => handleDelete(project.id)}
                    onRetryPipeline={() => void handleRetryPipeline(project.id)}
                    onRetrySyntax={() => void handleRetrySyntax(project.id)}
                    retrySyntaxing={retrySyntaxId === project.id}
                    isDeleting={deletingId === project.id}
                    isBeingDeletedByApi={deletingProjectId === project.id}
                    retryingPipeline={retryingId === project.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
);

ProjectsTableExtended.displayName = 'ProjectsTableExtended';

function SummaryBadge({
  label,
  value,
  color = 'default',
}: {
  label: string;
  value: number;
  color?: 'default' | 'success' | 'accent' | 'error' | 'warning';
}): JSX.Element {
  const colorStyles: Record<string, string> = {
    default: 'rgba(138, 155, 168, 0.2)',
    success: 'rgba(40, 167, 69, 0.2)',
    accent: 'rgba(47, 164, 181, 0.2)',
    error: 'rgba(220, 53, 69, 0.2)',
    warning: 'rgba(255, 193, 7, 0.2)',
  };

  const textColors: Record<string, string> = {
    default: 'var(--text-secondary)',
    success: '#28a745',
    accent: 'var(--accent-primary)',
    error: '#dc3545',
    warning: '#ffc107',
  };

  if (value === 0 && color !== 'default') return <></>;

  return (
    <span
      className="summary-badge"
      style={{
        background: colorStyles[color],
        color: textColors[color],
      }}
    >
      {value} {label}
    </span>
  );
}
